import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  InputAdornment,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Grid
} from "@mui/material";
import { Search as SearchIcon, Edit as EditIcon } from "@mui/icons-material";

const TransferModal = ({ transferDetails, handleEdit }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');


  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const filteredTransferDetails = Array.isArray(transferDetails)
    ? transferDetails.filter((item) => {
      const vesselName = item.contractDetails?.ShipDetails?.vesselname?.toLowerCase() || "";
      const crewName = item.contractDetails?.crewdetails?.name?.toLowerCase() || "";
      return vesselName.includes(searchQuery) || crewName.includes(searchQuery);
    })
    : [];



  const filteredCocData = filteredTransferDetails.filter((row) => {
    const cocDateRaw = row.contractStatusAfterForm1?.transferDate?.[0]?.Date || row.contractStatusAfterForm1?.transferDate?.[0]?.date;
    if (!cocDateRaw) return false;

    const cocDate = new Date(cocDateRaw);
    const month = String(cocDate.getMonth() + 1).padStart(2, '0'); // '03' for March
    const year = String(cocDate.getFullYear());

    // console.log({ month, year, selectedMonth, selectedYear, cocDateRaw });

    return (
      (!selectedMonth || month === selectedMonth) &&
      (!selectedYear || year === String(selectedYear))
    );
  });

  return (
    <Box mt={3} width="100%" p={2}>
      {/* Search Bar & Title */}
      <Box
        sx={{
          p: 1,
          bgcolor: "primary.main",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "nowrap",
        }}
      >
        {/* Search Input */}
        <TextField
          placeholder="Search by Vessel Name or Crew Name"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            bgcolor: "#f5f5f5",
            borderRadius: 2,
            width: 280,
          }}
        />

        {/* Title */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{
            flexShrink: 0,
            mx: 2,
            whiteSpace: "nowrap",
          }}
        >
          Transfer Details
        </Typography>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <FormControl size="small" sx={{ bgcolor: "white", borderRadius: 1, minWidth: 200 }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              label="Month"
            >
              {months.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ bgcolor: "white", borderRadius: 1, minWidth: 200 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              label="Year"
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Total Count */}
        <Typography
          variant="h6"
          component="h2"
          fontWeight="500"
          sx={{ whiteSpace: "nowrap" }}
        >
          Total Count: {filteredCocData.length}
        </Typography>
      </Box>


      {/* Table Section */}
      <Paper elevation={3} sx={{ p: 2 }}>
        {filteredCocData.length > 0 ? (
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Sr. No.</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Contract Details</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCocData.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Vessel Name: {item.contractDetails?.ShipDetails?.vesselname || "N/A"}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold">
                      <strong style={{ color: "#2e7d32" }}>
                        Company Name: {item.contractDetails?.vesselOwner?.company_shortname || "N/A"}
                      </strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Crew: {item.contractDetails?.crewdetails?.name || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Agent: {item.contractDetails?.crewingAgent?.agentName || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Transfer Date:{" "}
                      {item.contractStatusAfterForm1?.transfer[0]?.Date
                        ? new Date(item.contractStatusAfterForm1.transfer[0].Date).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                        : "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleEdit(item.contractDetails?._id)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography color="textSecondary" align="center">
            No transfer details found.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default TransferModal;
